// =============================================================================
// FILE: packages/renderer/src/components/files/ProjectFileExplorer.tsx
// PURPOSE: Dockable, read-only Project File Explorer (PFE Phase 1).
//   • Right-side drawer: hidden / docked (320 px) / fullscreen
//   • Single click selects a node, populating the preview pane
//   • Double click expands fullscreen preview
//   • Dock state + width persisted in localStorage
//   • Tree subscribes to pfe.treeChanged push events via the hook
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  Folder, FolderOpen, File, FileText, FileImage, FileCode2, FileType,
  ChevronRight, ChevronDown, X, Maximize2, Minimize2, RefreshCw, Hash,
} from 'lucide-react';
import {
  useProjectFileTree, useFileNodeMeta, useFilePreview,
  type FileTreeNode, type FileTreeFileNode, type FileTreeDirNode,
} from '../../hooks/useProjectFileTree.js';

type DockState = 'hidden' | 'docked' | 'fullscreen';

const WIDTH_KEY = 'pfe.dockWidth';
const STATE_KEY = 'pfe.dockState';

function loadInt(key: string, fallback: number): number {
  const v = parseInt(localStorage.getItem(key) ?? '', 10);
  return Number.isFinite(v) && v > 0 ? v : fallback;
}

export function ProjectFileExplorer({
  projectId, open, onOpenChange,
}: {
  projectId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}): JSX.Element | null {
  const [width, setWidth]   = useState<number>(() => loadInt(WIDTH_KEY, 640));
  const [dockState, setDockState] = useState<DockState>(() =>
    (localStorage.getItem(STATE_KEY) as DockState | null) ?? 'docked');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => { localStorage.setItem(WIDTH_KEY, String(width)); }, [width]);
  useEffect(() => { localStorage.setItem(STATE_KEY, dockState); }, [dockState]);

  const tree = useProjectFileTree(projectId);
  const meta = useFileNodeMeta(selectedId);
  const preview = useFilePreview(selectedId);

  if (!open) return null;
  if (!projectId) {
    return (
      <Drawer width={width} dockState={dockState}
        onResize={setWidth}
        onClose={() => onOpenChange(false)}
        onToggleFullscreen={() => setDockState((s) => s === 'fullscreen' ? 'docked' : 'fullscreen')}
        title="Project Files"
      >
        <div className="p-4 text-[11px] text-zinc-500">
          Select a project to view its files.
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer width={width} dockState={dockState}
      onResize={setWidth}
      onClose={() => onOpenChange(false)}
      onToggleFullscreen={() => setDockState((s) => s === 'fullscreen' ? 'docked' : 'fullscreen')}
      title={`Project · ${projectId}`}
      onRefresh={() => tree.refetch()}
    >
      <div className={
        dockState === 'fullscreen'
          ? 'flex flex-row gap-0 flex-1 min-h-0'
          : 'flex flex-col flex-1 min-h-0'
      }>
        <div className={
          dockState === 'fullscreen'
            ? 'w-72 flex-shrink-0 overflow-y-auto border-r border-zinc-900'
            : 'flex-1 overflow-y-auto'
        }>
          {tree.isLoading && <div className="p-3 text-[11px] text-zinc-500">Loading tree…</div>}
          {tree.isError && <div className="p-3 text-[11px] text-rose-300">Failed to load tree.</div>}
          {tree.data && tree.data.type === 'dir' && (
            <TreeView root={tree.data} selectedId={selectedId} onSelect={setSelectedId} />
          )}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto border-t border-zinc-900 lg:border-t-0">
          <FilePreviewPane meta={meta.data ?? null} preview={preview.data ?? null}
            loading={preview.isFetching} />
        </div>
      </div>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Drawer chrome — handles dock state + manual resize.
// ---------------------------------------------------------------------------
function Drawer({
  width, dockState, onResize, onClose, onToggleFullscreen, onRefresh, title, children,
}: {
  width: number; dockState: DockState;
  onResize: (n: number) => void;
  onClose: () => void;
  onToggleFullscreen: () => void;
  onRefresh?: () => void;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  const cls = dockState === 'fullscreen'
    ? 'fixed inset-0 z-[9700] flex flex-col bg-zinc-950 border-l border-cyan-400/30'
    : 'fixed top-0 right-0 bottom-0 z-40 flex flex-col bg-zinc-950 border-l border-cyan-400/30';
  const style = dockState === 'fullscreen' ? undefined : { width };

  // Drag-to-resize on the left edge.
  const onDragStart = (e: React.MouseEvent) => {
    if (dockState === 'fullscreen') return;
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX;
      onResize(Math.min(720, Math.max(280, startW + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <aside data-testid="pfe-drawer" className={cls} style={style}>
      {dockState !== 'fullscreen' && (
        <div
          data-testid="pfe-resize-handle"
          onMouseDown={onDragStart}
          className="absolute top-0 left-0 h-full w-[3px] cursor-ew-resize hover:bg-cyan-400/40"
        />
      )}
      <header className="px-3 py-1.5 flex items-center gap-2 border-b border-cyan-400/20 bg-zinc-950">
        <FolderOpen className="h-4 w-4 text-cyan-300" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-300/80">Project Files</div>
          <div className="text-[12px] font-semibold text-zinc-100 truncate">{title}</div>
        </div>
        {onRefresh && (
          <button data-testid="pfe-refresh" onClick={onRefresh}
            className="p-1 text-zinc-400 hover:text-cyan-300"
            title="Refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
        )}
        <button data-testid="pfe-fullscreen" onClick={onToggleFullscreen}
          className="p-1 text-zinc-400 hover:text-cyan-300"
          title={dockState === 'fullscreen' ? 'Dock' : 'Fullscreen'}>
          {dockState === 'fullscreen' ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
        <button data-testid="pfe-close" onClick={onClose}
          className="p-1 text-zinc-400 hover:text-rose-300" title="Close">
          <X className="h-3.5 w-3.5" />
        </button>
      </header>
      {children}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------
function TreeView({ root, selectedId, onSelect }: {
  root: FileTreeDirNode;
  selectedId: string | null;
  onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <ul className="py-1 px-1">
      {root.children.map((child) => (
        <TreeNode key={`${child.type}-${child.path}`} node={child} depth={0}
          selectedId={selectedId} onSelect={onSelect} />
      ))}
    </ul>
  );
}

function TreeNode({ node, depth, selectedId, onSelect }: {
  node: FileTreeNode; depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(depth < 1);
  if (node.type === 'dir') {
    return (
      <li>
        <button
          data-testid={`pfe-dir-${node.path}`}
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-1 px-1 py-0.5 text-[11.5px] text-zinc-200 hover:bg-zinc-900 rounded-sm"
          style={{ paddingLeft: 4 + depth * 12 }}
        >
          {open ? <ChevronDown className="h-3 w-3 text-zinc-500" /> : <ChevronRight className="h-3 w-3 text-zinc-500" />}
          {open ? <FolderOpen className="h-3.5 w-3.5 text-cyan-300" /> : <Folder className="h-3.5 w-3.5 text-cyan-300" />}
          <span className="flex-1 text-left truncate">{node.name}</span>
          <span className="text-[9.5px] text-zinc-500 tabular-nums">{node.fileCount}</span>
        </button>
        {open && (
          <ul>
            {node.children.map((child) => (
              <TreeNode key={`${child.type}-${child.path}`} node={child}
                depth={depth + 1} selectedId={selectedId} onSelect={onSelect} />
            ))}
          </ul>
        )}
      </li>
    );
  }
  const selected = selectedId === node.fileId;
  return (
    <li>
      <button
        data-testid={`pfe-file-${node.fileId}`}
        onClick={() => onSelect(node.fileId)}
        className={
          'w-full flex items-center gap-1 px-1 py-0.5 text-[11.5px] hover:bg-zinc-900 rounded-sm ' +
          (selected ? 'bg-cyan-500/10 text-cyan-100' : 'text-zinc-300')
        }
        style={{ paddingLeft: 4 + depth * 12 + 12 }}
        title={node.path}
      >
        <FileIcon name={node.name} />
        <span className="flex-1 text-left truncate">{node.name}</span>
        {node.revision && <span className="text-[9.5px] text-amber-300/80 tabular-nums">r{node.revision}</span>}
      </button>
    </li>
  );
}

function FileIcon({ name }: { name: string }): JSX.Element {
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
  if (ext === '.pdf') return <FileType className="h-3.5 w-3.5 text-rose-300" />;
  if (['.png','.jpg','.jpeg','.gif','.webp','.svg','.bmp'].includes(ext)) return <FileImage className="h-3.5 w-3.5 text-emerald-300" />;
  if (['.json','.xml','.html','.htm','.yml','.yaml'].includes(ext)) return <FileCode2 className="h-3.5 w-3.5 text-amber-300" />;
  if (['.txt','.md','.log','.csv','.eml'].includes(ext)) return <FileText className="h-3.5 w-3.5 text-zinc-300" />;
  return <File className="h-3.5 w-3.5 text-zinc-400" />;
}

// ---------------------------------------------------------------------------
// Preview pane
// ---------------------------------------------------------------------------
function FilePreviewPane({ meta, preview, loading }: {
  meta: import('../../hooks/useProjectFileTree.js').NodeMeta | null;
  preview: import('../../hooks/useProjectFileTree.js').PreviewResult | null;
  loading: boolean;
}): JSX.Element {
  if (!meta) {
    return (
      <div className="p-4 text-[11px] text-zinc-500">
        Select a file in the tree to view metadata and preview.
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <MetadataBlock meta={meta} />
      <div className="flex-1 min-h-0 overflow-auto bg-zinc-950">
        {loading && <div className="p-3 text-[11px] text-zinc-500">Loading preview…</div>}
        {!loading && preview && <PreviewBody preview={preview} />}
      </div>
    </div>
  );
}

function MetadataBlock({ meta }: { meta: import('../../hooks/useProjectFileTree.js').NodeMeta }): JSX.Element {
  return (
    <div data-testid="pfe-meta" className="px-3 py-2 border-b border-zinc-900 text-[11px] text-zinc-300 space-y-1">
      <div className="flex items-center gap-2">
        <Hash className="h-3 w-3 text-cyan-300" />
        <span className="font-mono text-[10.5px] text-cyan-200 truncate">{meta.fileName}</span>
      </div>
      <Row k="Path"     v={meta.relativePath} mono />
      <Row k="Type"     v={`${meta.docType}${meta.discipline ? ' · ' + meta.discipline : ''}`} />
      <Row k="Version"  v={`v${meta.version}${meta.revision ? ' · rev ' + meta.revision : ''}`} />
      <Row k="Status"   v={meta.status} />
      <Row k="Source"   v={meta.source} />
      <Row k="Size"     v={meta.sizeBytes ? formatBytes(meta.sizeBytes) : '—'} />
      <Row k="Modified" v={new Date(meta.lastModified).toISOString().slice(0,16).replace('T',' ')} />
      <Row k="Checksum" v={meta.checksum.slice(0, 16) + '…'} mono />
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 w-20 flex-shrink-0">{k}</span>
      <span className={'text-[11px] flex-1 break-all ' + (mono ? 'font-mono text-zinc-100' : 'text-zinc-100')}>{v}</span>
    </div>
  );
}

function PreviewBody({ preview }: { preview: import('../../hooks/useProjectFileTree.js').PreviewResult }): JSX.Element {
  if (preview.kind === 'unsupported' || preview.kind === 'too_large') {
    return (
      <div className="p-4 text-[11px] text-zinc-500" data-testid="pfe-preview-unsupported">
        {preview.kind === 'too_large' ? 'File too large for inline preview' : 'No inline viewer for this file type'}
        {preview.reason && <div className="mt-1 text-[10.5px] text-zinc-600">{preview.reason}</div>}
      </div>
    );
  }
  if (preview.kind === 'pdf' && preview.base64) {
    const dataUrl = `data:application/pdf;base64,${preview.base64}`;
    return (
      <object
        data={`${dataUrl}#view=FitH&zoom=page-width`}
        type="application/pdf"
        className="w-full h-full min-h-[calc(100vh-72px)]"
        data-testid="pfe-preview-pdf"
      >
        <div className="p-4 text-[11px] text-zinc-500">PDF preview not supported in this environment.</div>
      </object>
    );
  }
  if (preview.kind === 'image' && preview.base64) {
    return (
      <div className="p-3 flex items-center justify-center" data-testid="pfe-preview-image">
        <img src={`data:${preview.mime};base64,${preview.base64}`}
             alt={preview.fileName}
             className="max-w-full max-h-[calc(100vh-200px)] border border-zinc-800" />
      </div>
    );
  }
  if (preview.kind === 'text' || preview.kind === 'json' || preview.kind === 'eml') {
    return (
      <pre data-testid="pfe-preview-text"
        className="p-3 text-[11px] font-mono text-zinc-200 whitespace-pre-wrap break-words leading-relaxed">
        {preview.text}
      </pre>
    );
  }
  return <div className="p-4 text-[11px] text-zinc-500">No preview.</div>;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

// ---------------------------------------------------------------------------
// Workspace-pref hook — last opened project for the explorer.
// ---------------------------------------------------------------------------
const ACTIVE_PROJECT_KEY = 'pfe.lastProjectId';
export function usePfeActiveProject(initial: string | null = null) {
  const [id, setId] = useState<string | null>(() => localStorage.getItem(ACTIVE_PROJECT_KEY) ?? initial);
  const stableSet = useMemo(() => (v: string | null) => {
    setId(v);
    if (v) localStorage.setItem(ACTIVE_PROJECT_KEY, v);
    else   localStorage.removeItem(ACTIVE_PROJECT_KEY);
  }, []);
  return [id, stableSet] as const;
}
